/**
 * ReceiptSettingsTab - Admin settings for receipt printing configuration
 * Configures company info, VAT, printers, and auto-print options
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Receipt, Save, RefreshCw, Printer, Building2, 
  FileText, QrCode, MessageSquare, Settings, CheckCircle2,
  Wifi, TestTube, CheckCircle, AlertCircle, Upload, ImageIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function ReceiptSettingsTab() {
  const [settings, setSettings] = useState({
    company_name: 'TOP QUALITY IMPORT PARTS PTE LTD',
    company_address_line1: 'SUITE 8, JAGDISH GOSAI COMPLEX',
    company_address_line2: 'Namaka, Nadi Fiji',
    tin: '2901609577',
    vat_rate: 12.5,
    footer_message: 'Vinaka - Thank You!',
    loyalty_signup_url: '',
    // Logo configuration - use local asset for production compatibility
    logo_url: '/logo.png',
    print_logo_on_receipt: true,  // Toggle to enable/disable logo on receipt
    // 6-Station printer configuration (Updated December 2025)
    // Station 1: Customer Receipt - Star TSP143IV SK wireless sticky
    customer_printer_ip: '192.168.10.85',
    customer_printer_model: 'Star TSP143IV SK',
    // Station 2: Barista - Star TSP143IV SK wireless sticky
    barista_printer_ip: '192.168.0.2',
    barista_printer_model: 'Star TSP143IV SK',
    // Station 3: Kitchen - Star TSP143IIIW wireless non-sticky
    kitchen_printer_ip: '192.168.0.3',
    kitchen_printer_model: 'Star TSP143IIIW',
    // Station 4: Juicer - Star TSP143IIIW wireless non-sticky
    juicer_printer_ip: '192.168.0.4',
    juicer_printer_model: 'Star TSP143IIIW',
    // Station 5: Kiosk - Star TSP143IV SK wireless sticky
    kiosk_printer_ip: '192.168.10.65',
    kiosk_printer_model: 'Star TSP143IV SK',
    // Station 6: Counter - Star TSP143IV SK wireless sticky
    counter_printer_ip: '192.168.10.66',
    counter_printer_model: 'Star TSP143IV SK',
    // Auto-print settings per station (all 6)
    auto_print_customer: true,
    auto_print_barista: true,
    auto_print_kitchen: true,
    auto_print_juicer: true,
    auto_print_kiosk: true,
    auto_print_counter: true,
    // Station category routing - MUST MATCH backend STATION_ROUTING
    // Kitchen: build-your-own, breakfast, lunch, salads, kids
    // Juicer: iced-drinks, smoothies-blends, milk-shakes
    // Barista: coffee only
    // Counter: pies, patties, breads, pastries
    // No print: condiments
    kitchen_categories: ['build-your-own', 'breakfast', 'lunch', 'salads', 'kids'],
    juicer_categories: ['iced-drinks', 'smoothies-blends', 'milk-shakes'],
    barista_categories: ['coffee'],
    counter_categories: ['pies', 'patties', 'breads', 'pastries']
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [logoError, setLogoError] = useState(false);
  const [receiptLogoError, setReceiptLogoError] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Default Bulaccino logo path
  const DEFAULT_LOGO = '/logo.png';

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'receipt_logo');

      const res = await api.post('/admin/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Use the full URL from the response
      const logoUrl = res.data.url.startsWith('/') ? res.data.url : `/${res.data.url}`;
      setSettings(prev => ({ ...prev, logo_url: logoUrl }));
      setLogoError(false);
      toast.success('Logo uploaded successfully');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to upload logo');
    }
    setUploadingLogo(false);
  };

  const useDefaultLogo = () => {
    setSettings(prev => ({ ...prev, logo_url: DEFAULT_LOGO }));
    setLogoError(false);
    toast.success('Using default Bulaccino logo');
  };

  useEffect(() => {
    fetchSettings();
    fetchCategories();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/admin/receipt-settings');
      setSettings(prev => ({ ...prev, ...res.data }));
      // Reset logo error states when settings load
      setLogoError(false);
      setReceiptLogoError(false);
    } catch (err) {
      console.error('Failed to fetch receipt settings');
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get('/menu/categories');
      setCategories(res.data);
    } catch (err) {
      console.error('Failed to fetch categories');
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/admin/receipt-settings', settings);
      toast.success('Receipt settings saved!');
    } catch (err) {
      toast.error('Failed to save settings');
    }
    setSaving(false);
  };

  const testPrinter = async (printerType) => {
    toast.info(`Testing ${printerType} printer connection...`);
    // In production, this would send a test print to the printer
    setTimeout(() => {
      toast.success(`${printerType} printer test sent! Check printer for output.`);
    }, 1500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="receipt-settings-tab">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl text-foreground">Receipt Settings</h1>
          <p className="text-muted-foreground mt-1">Configure customer receipts and kitchen tickets</p>
        </div>
        <Button 
          onClick={saveSettings} 
          disabled={saving}
          className="bg-primary hover:bg-primary/90"
          data-testid="save-receipt-settings"
        >
          {saving ? (
            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
          ) : (
            <><Save className="w-4 h-4 mr-2" /> Save Settings</>
          )}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Company Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card rounded-2xl p-6 border border-border"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-heading text-xl text-foreground">Company Information</h2>
              <p className="text-sm text-muted-foreground">Printed on customer receipts</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-foreground">Company Name</Label>
              <Input
                value={settings.company_name}
                onChange={(e) => setSettings(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder="Company Name"
                className="bg-background text-foreground border-border"
                data-testid="company-name-input"
              />
            </div>
            
            <div>
              <Label className="text-foreground">Address Line 1</Label>
              <Input
                value={settings.company_address_line1}
                onChange={(e) => setSettings(prev => ({ ...prev, company_address_line1: e.target.value }))}
                placeholder="Street address, suite, etc."
                className="bg-background text-foreground border-border"
              />
            </div>
            
            <div>
              <Label className="text-foreground">Address Line 2</Label>
              <Input
                value={settings.company_address_line2}
                onChange={(e) => setSettings(prev => ({ ...prev, company_address_line2: e.target.value }))}
                placeholder="City, State, Country"
                className="bg-background text-foreground border-border"
              />
            </div>
            
            <div>
              <Label className="text-foreground">TIN (Tax Identification Number)</Label>
              <Input
                value={settings.tin}
                onChange={(e) => setSettings(prev => ({ ...prev, tin: e.target.value }))}
                placeholder="Tax ID Number"
                className="bg-background text-foreground border-border"
                data-testid="tin-input"
              />
            </div>
            
            {/* Logo Configuration */}
            <div className="md:col-span-2 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
              <div className="flex items-center justify-between mb-4">
                <Label className="text-foreground font-semibold">Receipt Logo</Label>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground">Print logo on receipt</span>
                  <Switch
                    checked={settings.print_logo_on_receipt}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, print_logo_on_receipt: checked }))}
                    data-testid="print-logo-toggle"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                {/* Logo URL Input */}
                <div className="space-y-3">
                  <div>
                    <Label className="text-foreground text-sm">Logo URL (optional)</Label>
                    <Input
                      value={settings.logo_url}
                      onChange={(e) => {
                        setSettings(prev => ({ ...prev, logo_url: e.target.value }));
                        setLogoError(false); // Reset error when URL changes
                      }}
                      placeholder="https://..."
                      className="bg-background text-foreground border-border mt-1"
                      data-testid="logo-url-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Paste a direct image URL or use the options below
                    </p>
                  </div>
                  
                  {/* Upload and Default Logo Buttons */}
                  <div className="flex flex-wrap gap-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleLogoUpload}
                        disabled={uploadingLogo}
                      />
                      <span className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors ${
                        uploadingLogo 
                          ? 'bg-muted text-muted-foreground cursor-not-allowed' 
                          : 'bg-primary/10 text-primary border-primary/20 hover:bg-primary/20'
                      }`}>
                        {uploadingLogo ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4" />
                        )}
                        {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                      </span>
                    </label>
                    
                    <button
                      type="button"
                      onClick={useDefaultLogo}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border bg-accent/10 text-accent border-accent/20 hover:bg-accent/20 transition-colors"
                    >
                      <ImageIcon className="w-4 h-4" />
                      Use Default Logo
                    </button>
                  </div>
                </div>
                
                {/* Logo Preview */}
                <div className="flex items-center justify-center">
                  {settings.logo_url ? (
                    <div className="bg-white p-4 rounded-xl shadow-md border-2 border-dashed border-gray-200" style={{ minWidth: '180px', minHeight: '100px' }}>
                      {!logoError ? (
                        <img 
                          src={settings.logo_url} 
                          alt="Logo preview" 
                          className="max-h-20 max-w-[150px] w-auto h-auto object-contain mx-auto"
                          style={{ display: 'block' }}
                          onError={() => setLogoError(true)}
                          onLoad={() => setLogoError(false)}
                        />
                      ) : (
                        <div className="text-center p-4">
                          <p className="text-red-500 text-sm font-medium">Failed to load image</p>
                          <p className="text-xs text-gray-500 mt-1">Check URL is a direct image link</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground text-sm p-4 border border-dashed border-gray-300 rounded-xl" style={{ minWidth: '180px', minHeight: '100px' }}>
                      <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      No logo URL provided
                    </div>
                  )}
                </div>
              </div>
              
              {/* Receipt Logo Preview Note */}
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>Note:</strong> Logo will be automatically resized to fit receipt width (max 150px wide, 48px tall). 
                  Use a PNG or SVG with transparent background for best results.
                </p>
              </div>
              
              {settings.print_logo_on_receipt && settings.logo_url && (
                <div className="mt-3 flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <CheckCircle className="w-4 h-4" />
                  <span>Logo will be printed on receipts with white background</span>
                </div>
              )}
              {!settings.print_logo_on_receipt && (
                <div className="mt-3 flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                  <AlertCircle className="w-4 h-4" />
                  <span>Logo printing is disabled</span>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Tax & Footer Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card rounded-2xl p-6 border border-border"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-accent" />
            </div>
            <div>
              <h2 className="font-heading text-xl text-foreground">Tax & Footer</h2>
              <p className="text-sm text-muted-foreground">VAT rate and receipt footer</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-foreground">VAT Rate (%)</Label>
              <Input
                type="number"
                step="0.1"
                value={settings.vat_rate}
                onChange={(e) => setSettings(prev => ({ ...prev, vat_rate: parseFloat(e.target.value) || 0 }))}
                className="bg-background text-foreground border-border"
                data-testid="vat-rate-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Current: {settings.vat_rate}% VAT will be added to all orders
              </p>
            </div>
            
            <div>
              <Label className="text-foreground">Footer Message</Label>
              <Textarea
                value={settings.footer_message}
                onChange={(e) => setSettings(prev => ({ ...prev, footer_message: e.target.value }))}
                placeholder="Thank you message..."
                rows={2}
                className="bg-background text-foreground border-border"
              />
            </div>
            
            <div>
              <Label className="text-foreground">Loyalty Signup URL (for QR Code)</Label>
              <Input
                value={settings.loyalty_signup_url}
                onChange={(e) => setSettings(prev => ({ ...prev, loyalty_signup_url: e.target.value }))}
                placeholder="https://bulaccinocafe.com/loyalty"
                className="bg-background text-foreground border-border"
              />
              <p className="text-xs text-muted-foreground mt-1">
                QR code on receipt will link to this URL. Leave blank for auto-generated link.
              </p>
            </div>

            {/* Tax Invoice Notice */}
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">Tax Invoice Compliance</span>
              </div>
              <p className="text-sm text-green-600 dark:text-green-500 mt-1">
                All receipts will be marked as "TAX INVOICE" with your TIN and VAT breakdown.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Printer Configuration */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-card rounded-2xl p-6 border border-border lg:col-span-2"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Printer className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="font-heading text-xl text-foreground">4-Station Printer Setup</h2>
              <p className="text-sm text-muted-foreground">Star Micronics WiFi printers for each station</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Station 1: POS/Customer Receipt Printer */}
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-yellow-500 flex items-center justify-center text-white font-bold">1</div>
                <div>
                  <Label className="text-foreground font-semibold">POS/Customer Printer</Label>
                  <p className="text-xs text-yellow-700 dark:text-yellow-400">Star TSP143IV SK - Sticky Paper</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Input
                  value={settings.customer_printer_ip}
                  onChange={(e) => setSettings(prev => ({ ...prev, customer_printer_ip: e.target.value }))}
                  placeholder="192.168.1.100"
                  className="bg-background text-foreground border-border flex-1"
                  data-testid="customer-printer-ip"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => testPrinter('Customer')}
                  className="border-border"
                >
                  <TestTube className="w-4 h-4 mr-1" />
                  Test
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Full receipt for customer + sticky label for take-away bag
              </p>
            </div>
            
            {/* Station 2: Barista/Counter Printer */}
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-amber-600 flex items-center justify-center text-white font-bold">2</div>
                <div>
                  <Label className="text-foreground font-semibold">Barista/Counter Printer</Label>
                  <p className="text-xs text-amber-700 dark:text-amber-400">Star TSP143IV SK - Sticky Paper</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Input
                  value={settings.barista_printer_ip}
                  onChange={(e) => setSettings(prev => ({ ...prev, barista_printer_ip: e.target.value }))}
                  placeholder="192.168.1.101"
                  className="bg-background text-foreground border-border flex-1"
                  data-testid="barista-printer-ip"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => testPrinter('Barista')}
                  className="border-border"
                >
                  <TestTube className="w-4 h-4 mr-1" />
                  Test
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Coffee, Iced Drinks, Pastries/Cakes from front of house
              </p>
            </div>
            
            {/* Station 3: Kitchen Printer */}
            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-200 dark:border-orange-800">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center text-white font-bold">3</div>
                <div>
                  <Label className="text-foreground font-semibold">Kitchen Printer</Label>
                  <p className="text-xs text-orange-700 dark:text-orange-400">Star TSP143IIIW - Non-Sticky Paper</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Input
                  value={settings.kitchen_printer_ip}
                  onChange={(e) => setSettings(prev => ({ ...prev, kitchen_printer_ip: e.target.value }))}
                  placeholder="192.168.1.102"
                  className="bg-background text-foreground border-border flex-1"
                  data-testid="kitchen-printer-ip"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => testPrinter('Kitchen')}
                  className="border-border"
                >
                  <TestTube className="w-4 h-4 mr-1" />
                  Test
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Breakfast, Lunch, Salads - Hot food for chef to prepare
              </p>
            </div>
            
            {/* Station 4: Juicer/Smoothie Printer */}
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center text-white font-bold">4</div>
                <div>
                  <Label className="text-foreground font-semibold">Juice & Smoothie Printer</Label>
                  <p className="text-xs text-green-700 dark:text-green-400">Star TSP143IIIW - Non-Sticky Paper</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Input
                  value={settings.juicer_printer_ip}
                  onChange={(e) => setSettings(prev => ({ ...prev, juicer_printer_ip: e.target.value }))}
                  placeholder="192.168.1.103"
                  className="bg-background text-foreground border-border flex-1"
                  data-testid="juicer-printer-ip"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => testPrinter('Juicer')}
                  className="border-border"
                >
                  <TestTube className="w-4 h-4 mr-1" />
                  Test
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Smoothies, Fresh Mixed Juices, Milkshakes
              </p>
            </div>
            
            {/* Station 5: Kiosk Printer */}
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-800">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center text-white font-bold">5</div>
                <div>
                  <Label className="text-foreground font-semibold">Kiosk Printer</Label>
                  <p className="text-xs text-purple-700 dark:text-purple-400">Star TSP143IV SK - Sticky Paper</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Input
                  value={settings.kiosk_printer_ip}
                  onChange={(e) => setSettings(prev => ({ ...prev, kiosk_printer_ip: e.target.value }))}
                  placeholder="192.168.10.65"
                  className="bg-background text-foreground border-border flex-1"
                  data-testid="kiosk-printer-ip"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => testPrinter('Kiosk')}
                  className="border-border"
                >
                  <TestTube className="w-4 h-4 mr-1" />
                  Test
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Full receipt for self-service kiosk orders
              </p>
            </div>
            
            {/* Station 6: Counter Printer */}
            <div className="p-4 bg-rose-50 dark:bg-rose-900/20 rounded-xl border border-rose-200 dark:border-rose-800">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-rose-500 flex items-center justify-center text-white font-bold">6</div>
                <div>
                  <Label className="text-foreground font-semibold">Counter Printer</Label>
                  <p className="text-xs text-rose-700 dark:text-rose-400">Star TSP143IV SK - Sticky Paper</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Input
                  value={settings.counter_printer_ip}
                  onChange={(e) => setSettings(prev => ({ ...prev, counter_printer_ip: e.target.value }))}
                  placeholder="192.168.10.66"
                  className="bg-background text-foreground border-border flex-1"
                  data-testid="counter-printer-ip"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => testPrinter('Counter')}
                  className="border-border"
                >
                  <TestTube className="w-4 h-4 mr-1" />
                  Test
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Display case items - Pies, Patties, Breads, Pastries
              </p>
            </div>
          </div>

          {/* Printer Status Overview */}
          <div className="mt-6 p-4 bg-muted/50 rounded-xl space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Wifi className="w-4 h-4" />
              <span>Printer Connection Status</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${settings.customer_printer_ip ? 'bg-yellow-500' : 'bg-gray-400'}`} />
                <span className="text-foreground">Customer: {settings.customer_printer_ip || 'Not set'}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${settings.barista_printer_ip ? 'bg-amber-500' : 'bg-gray-400'}`} />
                <span className="text-foreground">Barista: {settings.barista_printer_ip || 'Not set'}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${settings.kitchen_printer_ip ? 'bg-orange-500' : 'bg-gray-400'}`} />
                <span className="text-foreground">Kitchen: {settings.kitchen_printer_ip || 'Not set'}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${settings.juicer_printer_ip ? 'bg-green-500' : 'bg-gray-400'}`} />
                <span className="text-foreground">Juicer: {settings.juicer_printer_ip || 'Not set'}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${settings.kiosk_printer_ip ? 'bg-purple-500' : 'bg-gray-400'}`} />
                <span className="text-foreground">Kiosk: {settings.kiosk_printer_ip || 'Not set'}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${settings.counter_printer_ip ? 'bg-rose-500' : 'bg-gray-400'}`} />
                <span className="text-foreground">Counter: {settings.counter_printer_ip || 'Not set'}</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Orders are automatically routed to the correct station based on item category. 6 stations configured.
            </p>
          </div>
        </motion.div>

        {/* Auto-Print Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-card rounded-2xl p-6 border border-border lg:col-span-2"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Settings className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h2 className="font-heading text-xl text-foreground">Auto-Print Options</h2>
              <p className="text-sm text-muted-foreground">Automatic printing when orders are placed - all 6 stations</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Station 1: POS/Customer */}
            <div className="flex items-center justify-between p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-yellow-500 flex items-center justify-center text-white font-bold text-sm">1</div>
                <div>
                  <p className="font-medium text-foreground">Customer Receipt</p>
                  <p className="text-xs text-muted-foreground">Full receipt with all items</p>
                </div>
              </div>
              <Switch
                checked={settings.auto_print_customer}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, auto_print_customer: checked }))}
                data-testid="auto-print-customer-switch"
              />
            </div>
            
            {/* Station 2: Barista */}
            <div className="flex items-center justify-between p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-600 flex items-center justify-center text-white font-bold text-sm">2</div>
                <div>
                  <p className="font-medium text-foreground">Barista Ticket</p>
                  <p className="text-xs text-muted-foreground">Hot coffee drinks</p>
                </div>
              </div>
              <Switch
                checked={settings.auto_print_barista}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, auto_print_barista: checked }))}
                data-testid="auto-print-barista-switch"
              />
            </div>
            
            {/* Station 3: Kitchen */}
            <div className="flex items-center justify-between p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-200 dark:border-orange-800">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center text-white font-bold text-sm">3</div>
                <div>
                  <p className="font-medium text-foreground">Kitchen Ticket</p>
                  <p className="text-xs text-muted-foreground">Hot food for chef</p>
                </div>
              </div>
              <Switch
                checked={settings.auto_print_kitchen}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, auto_print_kitchen: checked }))}
                data-testid="auto-print-kitchen-switch"
              />
            </div>
            
            {/* Station 4: Juicer */}
            <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center text-white font-bold text-sm">4</div>
                <div>
                  <p className="font-medium text-foreground">Juice & Smoothie Ticket</p>
                  <p className="text-xs text-muted-foreground">Juices, smoothies, shakes</p>
                </div>
              </div>
              <Switch
                checked={settings.auto_print_juicer}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, auto_print_juicer: checked }))}
                data-testid="auto-print-juicer-switch"
              />
            </div>
            
            {/* Station 5: Kiosk */}
            <div className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-800">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center text-white font-bold text-sm">5</div>
                <div>
                  <p className="font-medium text-foreground">Kiosk Receipt</p>
                  <p className="text-xs text-muted-foreground">Self-service orders</p>
                </div>
              </div>
              <Switch
                checked={settings.auto_print_kiosk}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, auto_print_kiosk: checked }))}
                data-testid="auto-print-kiosk-switch"
              />
            </div>
            
            {/* Station 6: Counter */}
            <div className="flex items-center justify-between p-4 bg-rose-50 dark:bg-rose-900/20 rounded-xl border border-rose-200 dark:border-rose-800">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-rose-500 flex items-center justify-center text-white font-bold text-sm">6</div>
                <div>
                  <p className="font-medium text-foreground">Counter Ticket</p>
                  <p className="text-xs text-muted-foreground">Pies, pastries, breads</p>
                </div>
              </div>
              <Switch
                checked={settings.auto_print_counter}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, auto_print_counter: checked }))}
                data-testid="auto-print-counter-switch"
              />
            </div>
          </div>

          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-700 dark:text-blue-400">
              <strong>How it works:</strong> When an order is placed (POS or approved Kiosk order), the system automatically parses items by category and sends print jobs to the appropriate printers. Kiosk orders will be marked "PAID" and "KIOSK ORDER" on the POS receipt.
            </p>
          </div>

          <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
            <p className="text-sm text-amber-700 dark:text-amber-400">
              <strong>Manual Print:</strong> You can always manually print receipts and station tickets 
              from the Orders tab using the print buttons, including reprints and cancelled order notices.
            </p>
          </div>
        </motion.div>
      </div>

      {/* Receipt Preview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-card rounded-2xl p-6 border border-border"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <FileText className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </div>
          <div>
            <h2 className="font-heading text-xl text-foreground">Receipt Preview</h2>
            <p className="text-sm text-muted-foreground">How your customer receipt will look</p>
          </div>
        </div>

        <div className="flex justify-center">
          <div className="bg-white text-black p-6 rounded-lg shadow-lg max-w-[300px] w-full font-mono text-xs">
            {/* Receipt Preview Content */}
            <div className="text-center border-b border-dashed border-gray-300 pb-4 mb-4">
              <p className="font-bold text-sm">*** TAX INVOICE ***</p>
              <p className="font-bold mt-2">{settings.company_name}</p>
              <p>{settings.company_address_line1}</p>
              <p>{settings.company_address_line2}</p>
              <p className="mt-1">TIN: {settings.tin}</p>
            </div>
            
            <div className="border-b border-dashed border-gray-300 pb-4 mb-4">
              <div className="flex justify-between">
                <span>Transaction:</span>
                <span>TXN-ABC12345</span>
              </div>
              <div className="flex justify-between">
                <span>Date:</span>
                <span>{new Date().toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Time:</span>
                <span>{new Date().toLocaleTimeString()}</span>
              </div>
            </div>
            
            <div className="border-b border-dashed border-gray-300 pb-4 mb-4">
              <div className="flex justify-between mb-1">
                <span>1x Flat White (M)</span>
                <span>$4.50</span>
              </div>
              <div className="flex justify-between mb-1">
                <span>1x Acai Bowl</span>
                <span>$12.00</span>
              </div>
            </div>
            
            <div className="border-b border-dashed border-gray-300 pb-4 mb-4">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>$16.50</span>
              </div>
              <div className="flex justify-between">
                <span>VAT ({settings.vat_rate}%):</span>
                <span>${(16.50 * settings.vat_rate / 100).toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold text-sm mt-2">
                <span>TOTAL:</span>
                <span>${(16.50 * (1 + settings.vat_rate / 100)).toFixed(2)}</span>
              </div>
            </div>
            
            <div className="border-b border-dashed border-gray-300 pb-4 mb-4">
              <div className="flex justify-between">
                <span>Payment:</span>
                <span>M-PAiSA</span>
              </div>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-gray-200 mx-auto mb-2 flex items-center justify-center">
                <QrCode className="w-12 h-12 text-gray-500" />
              </div>
              <p className="text-[10px]">Scan to join rewards</p>
              <p className="font-bold mt-4">{settings.footer_message}</p>
              
              {/* Logo at bottom - only on customer receipts */}
              {settings.print_logo_on_receipt && settings.logo_url && (
                <div className="mt-4 pt-4 border-t border-dashed border-gray-300">
                  <div className="bg-white p-2 rounded shadow-sm inline-block">
                    {!receiptLogoError ? (
                      <img 
                        src={settings.logo_url} 
                        alt="Bulaccino Cafe" 
                        className="h-12 w-auto object-contain mx-auto"
                        style={{ maxWidth: '100px' }}
                        onError={() => setReceiptLogoError(true)}
                      />
                    ) : (
                      <p className="text-red-500 text-xs">Logo unavailable</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
